/*
 * Brand carousel — drag / flick enhancement.
 * Progressively enhances the CSS marquee in sections/brand_carousel.liquid.
 * - Keeps the auto-scroll running.
 * - Lets the user grab and pull the track (mouse + touch), in either direction.
 * - A flick adds momentum: a hard swipe speeds it up, then it eases back to the
 *   normal auto-scroll speed. Works both ways (flick with the scroll = faster,
 *   flick against it = briefly reverses).
 * - Desktop keeps the original "pause on hover" behaviour.
 * - Falls back to the pure-CSS animation when JS is unavailable.
 */
(function () {
  'use strict';
  if (window.__brandCarouselDragInit) return;
  window.__brandCarouselDragInit = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCarousel(wrapper) {
    var viewport = wrapper.querySelector('.brand-carousel');
    var track = wrapper.querySelector('.brand-carousel-track');
    if (!viewport || !track || track.dataset.dragReady === '1') return;
    track.dataset.dragReady = '1';

    // Take over from the CSS animation.
    viewport.classList.add('is-draggable');

    var copies = parseInt(track.dataset.copies || '4', 10);
    if (!copies || copies < 1) copies = 1;

    var setWidth = 0; // width of a single copy of the logo set
    function measure() { setWidth = track.scrollWidth / copies; }
    measure();

    // Auto-scroll duration (seconds for one set), read live from the CSS vars so
    // the theme-editor speed sliders keep working.
    function autoSpeed() {
      if (reduceMotion) return 0;
      var cs = getComputedStyle(wrapper);
      var isMobile = window.matchMedia('(max-width: 749px)').matches;
      var secs = parseFloat(cs.getPropertyValue(isMobile ? '--carousel-speed-mobile' : '--carousel-speed'));
      if (!secs || secs <= 0) secs = 35;
      return -(setWidth / secs); // negative = scroll left, matching the original
    }

    var DRAG_THRESHOLD = 5; // px of movement before a press becomes a drag

    var pos = 0;          // current translateX (px)
    var momentum = 0;     // extra px/sec on top of auto speed, decays to 0
    var pressing = false; // pointer is down but hasn't moved enough to be a drag
    var dragging = false; // pointer is down AND has crossed the threshold
    var hovering = false;
    var pointerId = null;
    var startX = 0;
    var startY = 0;
    var lastX = 0;
    var lastMoveTime = 0;
    var dragVelocity = 0; // px/sec, smoothed, used to seed momentum on release
    var dragDistance = 0; // total px moved — used to swallow the click after a drag
    var lastFrame = null;

    function wrap() {
      if (setWidth <= 0) return;
      while (pos <= -setWidth) pos += setWidth;
      while (pos > 0) pos -= setWidth;
    }

    function render() { track.style.transform = 'translateX(' + pos + 'px)'; }

    function frame(now) {
      if (lastFrame === null) lastFrame = now;
      var dt = (now - lastFrame) / 1000;
      lastFrame = now;
      if (dt > 0.1) dt = 0.1; // clamp after a tab switch

      if (!dragging) {
        var base = hovering ? 0 : autoSpeed(); // pause auto while hovering (desktop)
        pos += (base + momentum) * dt;
        momentum *= Math.pow(0.06, dt);        // frame-rate-independent decay
        if (Math.abs(momentum) < 1) momentum = 0;
        wrap();
        render();
      }
      requestAnimationFrame(frame);
    }

    viewport.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      // Just arm a potential drag — don't capture the pointer or start dragging
      // yet, so a plain click/tap still reaches the logo's link.
      pressing = true;
      dragging = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      lastX = e.clientX;
      lastMoveTime = e.timeStamp;
      dragVelocity = 0;
      dragDistance = 0;
    });

    viewport.addEventListener('pointermove', function (e) {
      if (e.pointerId !== pointerId) return;
      if (!dragging) {
        if (!pressing) return;
        var totalDx = e.clientX - startX;
        var totalDy = e.clientY - startY;
        // Not moved far enough yet to tell a click from a drag.
        if (Math.abs(totalDx) < DRAG_THRESHOLD) return;
        // A mostly-vertical move means the user is scrolling the page — bail out.
        if (Math.abs(totalDy) > Math.abs(totalDx)) { pressing = false; return; }
        // Threshold crossed horizontally: promote to a real drag and grab it.
        dragging = true;
        momentum = 0;
        viewport.classList.add('is-dragging');
        try { viewport.setPointerCapture(pointerId); } catch (err) {}
        lastX = e.clientX;
        lastMoveTime = e.timeStamp;
        return;
      }
      var dx = e.clientX - lastX;
      var dt = (e.timeStamp - lastMoveTime) / 1000;
      lastX = e.clientX;
      lastMoveTime = e.timeStamp;
      pos += dx;
      dragDistance += Math.abs(dx);
      if (dt > 0) {
        var instV = dx / dt;
        dragVelocity = dragVelocity * 0.6 + instV * 0.4; // smooth
      }
      wrap();
      render();
      e.preventDefault();
    });

    function endDrag(e) {
      if (pointerId !== null && e.pointerId !== pointerId) return;
      var wasDragging = dragging;
      pressing = false;
      dragging = false;
      viewport.classList.remove('is-dragging');
      try { viewport.releasePointerCapture(pointerId); } catch (err) {}
      pointerId = null;
      if (!wasDragging) return; // a plain click/tap — let the link fire
      // Momentum is the flick velocity relative to the current base speed.
      var base = hovering ? 0 : autoSpeed();
      momentum = dragVelocity - base;
      var maxM = setWidth * 3; // cap to keep it sane
      if (momentum > maxM) momentum = maxM;
      if (momentum < -maxM) momentum = -maxM;
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    viewport.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') hovering = true;
    });
    viewport.addEventListener('pointerleave', function (e) {
      if (e.pointerType === 'mouse') { hovering = false; if (dragging) endDrag(e); }
    });

    // Don't let a drag trigger the logo's link.
    viewport.addEventListener('click', function (e) {
      if (dragDistance > 6) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // Block the browser's native link/image drag so pressing on a logo
    // drags the carousel (this is what otherwise hijacks the pointer).
    viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });

    window.addEventListener('resize', measure);
    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });

    requestAnimationFrame(frame);
  }

  function initAll() {
    document.querySelectorAll('.brand-carousel-wrapper').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
